import { CompanyProfile, HiringPreferences, JobPosting, CompanyApiResponse, DashboardData, DashboardStats, ProfileStat, JobStatistic, TodoItem, Interview, RecentJob } from '../types';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api';
const COMPANY_SERVICE_BASE = import.meta.env.VITE_COMPANY_SERVICE_URL || 'http://127.0.0.1:8001';
const JOB_SERVICE_BASE = import.meta.env.VITE_JOB_SERVICE_URL || 'http://localhost:8003';

// Company data from my-companies endpoint (matches Go backend CompanyDTO from company-service)
export interface MyCompanyData {
    id: string;
    name: string;
    industry: string;
    website?: string;
    description?: string;
    logoUrl?: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

// Company creation request type (matches backend CompanyCreateSchema)
export interface CreateCompanyRequest {
    name: string;
    website?: string;
    industry: string;
    companySize?: string;
    description?: string;
    domain?: string;
    logoUrl?: string;
}

// Company creation response (matches backend CompanyCreateResponse)
export interface CreateCompanyResponse {
    message: string;
    companyId: string;
}

class CompanyApiService {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<CompanyApiResponse<T>> {
        const url = `${API_BASE}${endpoint}`;
        const token = localStorage.getItem('authToken'); // ✅ Fixed: use 'authToken' not 'companyAuthToken'

        const config: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
        };

        try {
            const response = await fetch(url, config);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Company API request failed:', error);
            throw error;
        }
    }

    // ============================================
    // COMPANY CREATION (NEW)
    // ============================================

    /**
     * Create a new company with optional logo upload
     * Matches backend: POST /api/companies/
     *
     * @param companyData - Company information (name, industry, size, etc.)
     * @param logoFile - Optional logo image file
     * @returns Company ID of the created company
     */
    async createCompany(companyData: CreateCompanyRequest, accessToken?: string): Promise<CreateCompanyResponse> {
        try {
            // Use Company-Service endpoint (Port 8001)
            const url = `${COMPANY_SERVICE_BASE}/companies/v1/create`;
            const token = accessToken || localStorage.getItem('authToken');

            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }


            // Send as JSON (Company-Service expects JSON, not FormData)
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(companyData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Company creation failed:', errorData);
                throw new Error(errorData.error || errorData.message || `Failed to create company: ${response.status}`);
            }

            const result = await response.json();

            // Extract from payload wrapper if exists
            if (result.payload) {
                return {
                    message: 'Company created successfully',
                    companyId: result.payload.id || result.payload._id
                };
            }

            return {
                message: result.message || 'Company created successfully',
                companyId: result.id || result.companyId
            };

        } catch (error: any) {
            console.error('❌ Company creation failed:', error);
            throw new Error(error.message || 'Failed to create company');
        }
    }

    // Company Profile Methods
    async saveCompanyProfile(profile: CompanyProfile): Promise<void> {
        await this.request('/api/company/profile', {
            method: 'POST',
            body: JSON.stringify(profile),
        });
    }

    async getCompanyProfile(): Promise<CompanyProfile> {
        const response = await this.request<CompanyProfile>('/api/company/profile');
        return response.data;
    }

    // Hiring Preferences Methods
    async saveHiringPreferences(preferences: HiringPreferences): Promise<void> {
        await this.request('/api/company/hiring-preferences', {
            method: 'POST',
            body: JSON.stringify(preferences),
        });
    }

    // Job Posting Methods
    async createJobPosting(jobData: JobPosting): Promise<{ jobId: string }> {
        const response = await this.request<{ jobId: string }>('/api/company/jobs', {
            method: 'POST',
            body: JSON.stringify(jobData),
        });
        return response.data;
    }

    // ============================================
    // DASHBOARD DATA METHODS
    // ============================================

    /**
     * Get complete dashboard data including stats, charts, and listings
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard
     * - Headers: Authorization: Bearer {token}
     * - Response: DashboardData object
     * - Error handling: 401 (unauthorized), 500 (server error)
     */
    async getDashboardData(): Promise<DashboardData> {
        try {
            // Fetch real recent jobs from job-service
            const recentJobs = await this.getRecentJobs(5);

            // Return dashboard data with real jobs and mock data for other sections
            return {
                stats: MOCK_DASHBOARD_DATA.stats,
                profileStats: MOCK_DASHBOARD_DATA.profileStats,
                jobStatistics: MOCK_DASHBOARD_DATA.jobStatistics,
                todoList: MOCK_DASHBOARD_DATA.todoList,
                todayInterviews: MOCK_DASHBOARD_DATA.todayInterviews,
                recentJobs: recentJobs, // Use real jobs here
            };
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            // Fallback to mock data for development
            return MOCK_DASHBOARD_DATA;
        }
    }

    /**
     * Get dashboard statistics (top cards)
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard/stats
     * - Response format: { candidatesToReview: number, messagesReceived: number, interviews: number }
     */
    async getDashboardStats(): Promise<DashboardStats> {
        try {
            // TODO: Uncomment when backend is ready
            // const response = await this.request<DashboardStats>('/api/company/dashboard/stats');
            // return response.data;

            return MOCK_DASHBOARD_DATA.stats;
        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error);
            return MOCK_DASHBOARD_DATA.stats;
        }
    }

    /**
     * Get profile statistics (job posts, match quality, applicants, pipeline)
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard/profile-stats
     * - Response: Array of ProfileStat objects
     */
    async getProfileStats(): Promise<ProfileStat[]> {
        try {
            // TODO: Uncomment when backend is ready
            // const response = await this.request<ProfileStat[]>('/api/company/dashboard/profile-stats');
            // return response.data;

            return MOCK_DASHBOARD_DATA.profileStats;
        } catch (error) {
            console.error('Failed to fetch profile stats:', error);
            return MOCK_DASHBOARD_DATA.profileStats;
        }
    }

    /**
     * Get job statistics for chart (weekly data)
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard/job-statistics?period=week
     * - Query params: period (day|week|month)
     * - Response: Array of JobStatistic objects
     */
    async getJobStatistics(_period: 'day' | 'week' | 'month' = 'week'): Promise<JobStatistic[]> {
        try {
            // TODO: Uncomment when backend is ready
            // const response = await this.request<JobStatistic[]>(`/api/company/dashboard/job-statistics?period=${period}`);
            // return response.data;

            return MOCK_DASHBOARD_DATA.jobStatistics;
        } catch (error) {
            console.error('Failed to fetch job statistics:', error);
            return MOCK_DASHBOARD_DATA.jobStatistics;
        }
    }

    /**
     * Get today's scheduled interviews
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard/interviews/today
     * - Response: Array of Interview objects
     */
    async getTodayInterviews(): Promise<Interview[]> {
        try {
            // TODO: Uncomment when backend is ready
            // const response = await this.request<Interview[]>('/api/company/dashboard/interviews/today');
            // return response.data;

            return MOCK_DASHBOARD_DATA.todayInterviews;
        } catch (error) {
            console.error('Failed to fetch today interviews:', error);
            return MOCK_DASHBOARD_DATA.todayInterviews;
        }
    }

    /**
     * Get todo list items
     *
     * TODO: Backend Integration
     * - Endpoint: GET /api/company/dashboard/todos
     * - Response: Array of TodoItem objects
     */
    async getTodoList(): Promise<TodoItem[]> {
        try {
            // TODO: Uncomment when backend is ready
            // const response = await this.request<TodoItem[]>('/api/company/dashboard/todos');
            // return response.data;

            return MOCK_DASHBOARD_DATA.todoList;
        } catch (error) {
            console.error('Failed to fetch todo list:', error);
            return MOCK_DASHBOARD_DATA.todoList;
        }
    }

    /**
     * Get active jobs count for the company
     * Fetches all jobs and counts how many are active
     *
     * @returns Object with total jobs count and active jobs count
     */
    async getActiveJobsCount(): Promise<{ total: number; active: number }> {
        try {
            // Get company ID from localStorage
            const companyDataString = localStorage.getItem('companyData');
            if (!companyDataString) {
                console.warn('No company data found, returning zero counts');
                return { total: 0, active: 0 };
            }

            const companyData = JSON.parse(companyDataString);
            const companyId = companyData.id || companyData._id;

            if (!companyId) {
                console.warn('No company ID found in company profile, returning zero counts');
                return { total: 0, active: 0 };
            }

            // Fetch jobs for this company using the dedicated endpoint
            const url = `${JOB_SERVICE_BASE}/jobs/v1/company/${companyId}`;

            const response = await axios.get(url);

            // Handle Go service response format
            if (response.data && !response.data.status?.error) {
                const companyJobs = response.data.payload || response.data.data || [];
                const total = companyJobs.length;
                const active = companyJobs.filter((job: any) =>
                    job.is_active !== false &&
                    job.isActive !== false &&
                    job.status !== 'closed'
                ).length;

                return { total, active };
            }

            return { total: 0, active: 0 };
        } catch (error) {
            console.error('Failed to fetch active jobs count:', error);
            return { total: 0, active: 0 };
        }
    }

    /**
     * Get recently posted jobs from job-service
     * Fetches all jobs for the company, sorted by creation date
     *
     * @param limit - Number of recent jobs to fetch (default 5)
     * @returns Array of RecentJob objects
     */
    async getRecentJobs(limit: number = 5): Promise<RecentJob[]> {
        try {
            // Get company ID from localStorage
            const companyDataString = localStorage.getItem('companyData');
            if (!companyDataString) {
                console.warn('No company data found, returning empty jobs');
                return [];
            }

            const companyData = JSON.parse(companyDataString);
            const companyId = companyData.id || companyData._id;

            if (!companyId) {
                console.warn('No company ID found in company profile, returning empty jobs');
                return [];
            }

            // Fetch jobs for this company using the dedicated endpoint
            const url = `${JOB_SERVICE_BASE}/jobs/v1/company/${companyId}`;

            const response = await axios.get(url);

            // Handle Go service response format
            if (response.data && !response.data.status?.error) {
                const companyJobs = response.data.payload || response.data.data || [];

                // Sort by creation date (most recent first) and take limit
                const sortedJobs = companyJobs
                    .sort((a: any, b: any) => {
                        const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
                        const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
                        return dateB - dateA;
                    })
                    .slice(0, limit);

                // Transform to RecentJob format
                return sortedJobs.map((job: any) => ({
                    id: job.id || job._id || '',
                    title: job.title || 'Untitled Job',
                    type: job.employment_type || job.employmentType || 'Full Time',
                    daysRemaining: this.calculateDaysRemaining(job.created_at || job.createdAt),
                    status: (job.is_active !== false && job.isActive !== false && job.status !== 'closed') ? 'active' : 'closed',
                    applications: 0, // TODO: Fetch from applications service
                    salaryMin: this.extractSalaryMin(job.salary_range || job.salaryRange),
                    salaryMax: this.extractSalaryMax(job.salary_range || job.salaryRange),
                }));
            }

            console.warn('No jobs found or error in response');
            return [];
        } catch (error) {
            console.error('Failed to fetch recent jobs from job-service:', error);
            return [];
        }
    }

    /**
     * Calculate days remaining for a job posting (30 days from creation)
     */
    private calculateDaysRemaining(createdAt?: string): number {
        if (!createdAt) return 30;
        const created = new Date(createdAt);
        const now = new Date();
        const daysSinceCreation = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
        return Math.max(0, 30 - daysSinceCreation);
    }

    /**
     * Extract minimum salary from salary range string
     */
    private extractSalaryMin(salaryRange?: string): number {
        if (!salaryRange) return 0;
        const match = salaryRange.match(/(\d+)/);
        return match ? parseInt(match[1]) : 0;
    }

    /**
     * Extract maximum salary from salary range string
     */
    private extractSalaryMax(salaryRange?: string): number {
        if (!salaryRange) return 0;
        const numbers = salaryRange.match(/(\d+)/g);
        return numbers && numbers.length > 1 ? parseInt(numbers[1]) : (numbers ? parseInt(numbers[0]) * 1.5 : 0);
    }

    /**
     * Get candidates to review count for the company
     * Fetches application statistics from job-service
     *
     * @returns Object with total candidates and breakdown by stage
     */
    async getCandidatesToReview(): Promise<{ total: number; byStage: Record<string, number> }> {
        try {
            // Get company ID from localStorage
            const companyDataString = localStorage.getItem('companyData');
            if (!companyDataString) {
                console.warn('No company data found, returning zero counts');
                return { total: 0, byStage: {} };
            }

            const companyData = JSON.parse(companyDataString);
            const companyId = companyData.id || companyData._id;

            if (!companyId) {
                console.warn('No company ID found in company profile, returning zero counts');
                return { total: 0, byStage: {} };
            }

            // Fetch application stats for this company
            const token = localStorage.getItem('authToken');
            const url = `${JOB_SERVICE_BASE}/applications/v1/company/${companyId}/stats`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            // Handle Go service response format
            if (response.data && !response.data.status?.error) {
                const stats = response.data.payload || response.data;
                return {
                    total: stats.total || 0,
                    byStage: stats.byStage || {},
                };
            }

            return { total: 0, byStage: {} };
        } catch (error) {
            console.error('Failed to fetch candidates to review:', error);
            return { total: 0, byStage: {} };
        }
    }

    /**
     * Get all applications for the company
     * Fetches applications list from job-service
     *
     * @param stage - Optional stage filter (applied, interview, offered, rejected)
     * @returns Array of applications
     */
    async getCompanyApplications(stage?: string): Promise<any[]> {
        try {
            // Get company ID from localStorage
            const companyDataString = localStorage.getItem('companyData');
            if (!companyDataString) {
                console.warn('No company data found, returning empty applications');
                return [];
            }

            const companyData = JSON.parse(companyDataString);
            const companyId = companyData.id || companyData._id;

            if (!companyId) {
                console.warn('No company ID found in company profile, returning empty applications');
                return [];
            }

            // Fetch applications for this company
            const token = localStorage.getItem('authToken');
            let url = `${JOB_SERVICE_BASE}/applications/v1/company/${companyId}`;
            if (stage) {
                url += `?stage=${stage}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            // Handle Go service response format
            if (response.data && !response.data.status?.error) {
                const result = response.data.payload || response.data;
                return result.data || [];
            }

            return [];
        } catch (error) {
            console.error('Failed to fetch company applications:', error);
            return [];
        }
    }
}

// ============================================
// MOCK DATA FOR DEVELOPMENT
// Remove this section when backend is integrated
// ============================================

const MOCK_DASHBOARD_DATA: DashboardData = {
    stats: {
        candidatesToReview: 76,
        messagesReceived: 34,
        interviews: 12,
    },
    profileStats: [
        {
            id: '1',
            title: 'Active Job Posts',
            value: '78%',
            icon: 'gauge',
            iconBg: 'rgba(139, 205, 255, 0.5)',
            status: 'complete',
            statusBg: '#C5E6FF',
            chart: [0, 20, 40, 30, 50, 45, 65],
            chartColor: '#015FA6',
        },
        {
            id: '2',
            title: 'Average Match Quality',
            value: 64,
            icon: 'crosshair',
            iconBg: 'rgba(251, 187, 37, 0.17)',
            status: 'normal',
            statusBg: '#FFF4DA',
            chart: [10, 25, 30, 20, 35, 40, 45],
            chartColor: '#FBBB25',
        },
        {
            id: '3',
            title: 'New Applicants This Week',
            value: 16,
            subtitle: 'Ongoing',
            icon: 'users',
            iconBg: 'rgba(69, 131, 48, 0.27)',
            status: 'great',
            statusBg: '#458330',
            chart: [5, 15, 20, 25, 30, 35, 40],
            chartColor: '#458330',
        },
        {
            id: '4',
            title: 'Interview Pipeline',
            value: 23,
            subtitle: 'in the Last 7 Days',
            icon: 'eye',
            iconBg: 'rgba(255, 82, 16, 0.17)',
            status: 'normal',
            statusBg: '#FFE2D7',
            chart: [8, 18, 25, 20, 28, 35, 42],
            chartColor: '#FF5210',
        },
    ],
    jobStatistics: [
        { day: 'Sun', jobViews: 1200, jobApplied: 800, jobOpened: 600 },
        { day: 'Mon', jobViews: 1800, jobApplied: 1200, jobOpened: 900 },
        { day: 'Tue', jobViews: 2400, jobApplied: 1600, jobOpened: 1200 },
        { day: 'Wed', jobViews: 2000, jobApplied: 1400, jobOpened: 1000 },
        { day: 'Thu', jobViews: 2800, jobApplied: 1800, jobOpened: 1400 },
        { day: 'Fri', jobViews: 3200, jobApplied: 2200, jobOpened: 1600 },
        { day: 'Sat', jobViews: 2600, jobApplied: 1800, jobOpened: 1300 },
    ],
    todoList: [
        {
            id: '1',
            date: '13 October, 2025',
            title: 'Uideck Yearly Meetings',
            status: 'completed',
        },
        {
            id: '2',
            date: '13 October, 2025',
            title: '2024 Dribbble Meet Up',
            status: 'upcoming',
        },
        {
            id: '3',
            date: '14 February, 2024',
            title: 'Plain Admin Dashboard Meeting',
            status: 'completed',
        },
        {
            id: '4',
            date: '14 February, 2024',
            title: 'Uideck Yearly Meetings',
            time: '10:20 AM - 3:00 PM',
            status: 'canceled',
        },
    ],
    todayInterviews: [
        {
            id: '1',
            candidateName: 'Kathryn Murphy',
            position: 'UI/UX Designer',
            startTime: '10:30 AM',
            endTime: '11:30 AM',
            meetingLink: 'https://meet.google.com/abc-defg-hij',
        },
        {
            id: '2',
            candidateName: 'Robert Fox',
            position: 'Frontend Developer',
            startTime: '01:00 PM',
            endTime: '02:00 PM',
            meetingLink: 'https://meet.google.com/xyz-abcd-efg',
        },
        {
            id: '3',
            candidateName: 'Jenny Wilson',
            position: 'Product Designer',
            startTime: '03:30 PM',
            endTime: '04:30 PM',
            meetingLink: 'https://meet.google.com/pqr-stuv-wxy',
        },
        {
            id: '4',
            candidateName: 'Darrell Steward',
            position: 'Backend Developer',
            startTime: '05:00 PM',
            endTime: '06:00 PM',
            meetingLink: 'https://meet.google.com/mno-pqrs-tuv',
        },
        {
            id: '5',
            candidateName: 'Kristin Watson',
            position: 'Full Stack Developer',
            startTime: '06:30 PM',
            endTime: '07:30 PM',
            meetingLink: 'https://meet.google.com/def-ghij-klm',
        },
    ],
    recentJobs: [
        {
            id: '1',
            title: 'UI/UX Designer',
            type: 'Full Time',
            daysRemaining: 27,
            status: 'active',
            applications: 798,
            salaryMin: 11,
            salaryMax: 22,
        },
        {
            id: '2',
            title: 'Senior Frontend Developer',
            type: 'Full Time',
            daysRemaining: 15,
            status: 'active',
            applications: 542,
            salaryMin: 18,
            salaryMax: 30,
        },
        {
            id: '3',
            title: 'Product Manager',
            type: 'Full Time',
            daysRemaining: 20,
            status: 'active',
            applications: 321,
            salaryMin: 25,
            salaryMax: 40,
        },
        {
            id: '4',
            title: 'Backend Developer',
            type: 'Contract',
            daysRemaining: 10,
            status: 'active',
            applications: 412,
            salaryMin: 15,
            salaryMax: 28,
        },
        {
            id: '5',
            title: 'DevOps Engineer',
            type: 'Full Time',
            daysRemaining: 18,
            status: 'active',
            applications: 289,
            salaryMin: 20,
            salaryMax: 35,
        },
    ],
};

// ============================================
// TEAM INVITATION TYPES (Go Backend)
// ============================================

export interface InviteRecruiterRequest {
    email: string;
}

export interface InviteRecruiterResponse {
    success: boolean;
    message: string;
    invitationId: string;
    email: string;
}

export interface CompanyMember {
    id: string;
    email: string;
    userId?: string;
    status: 'pending' | 'approved' | 'expired';
    requestedAt: string;
    approvedAt?: string;
}

export interface CompanyStatsResponse {
    totalJobs: number;
    activeJobs: number;
    totalApplications: number;
    pendingInvitations: number;
    totalMembers: number;
    approvedMembers: number;
}

// Extend the CompanyApiService class with Go backend methods
class CompanyApiServiceExtended extends CompanyApiService {

    // ============================================
    // TEAM MANAGEMENT (Go Backend)
    // ============================================

    /**
     * Send invitation to recruiter to join company team
     * Calls Go backend: POST /api/v1/companies/:id/invite
     *
     * @param companyId - Company UUID
     * @param email - Recruiter email address
     * @returns Invitation details with invitation ID
     */
    async inviteRecruiter(companyId: string, email: string): Promise<InviteRecruiterResponse> {
        try {
            // Fixed URL: Backend route is /invitations/v1/:companyId/create (not /api/v1/companies/:id/invite)
            const url = `${COMPANY_SERVICE_BASE}/invitations/v1/${companyId}/create`;
            const token = localStorage.getItem('authToken');

            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }


            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    email,
                    role: 'recruiter' // Role for the invited person (recipient)
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Invitation failed:', errorData);
                throw new Error(errorData.message || errorData.error || `Failed to send invitation: ${response.status}`);
            }

            const result = await response.json();

            // Extract data from payload wrapper if exists
            if (result.payload) {
                return result.payload;
            }

            return result.data || result;

        } catch (error: any) {
            console.error('❌ Failed to send invitation:', error);
            throw new Error(error.message || 'Failed to send invitation');
        }
    }

    /**
     * Get all company members (pending and approved)
     * Calls Go backend: GET /members/v1/:companyId/list
     *
     * @param companyId - Company UUID
     * @returns Array of company members
     */
    async getCompanyMembers(companyId: string): Promise<CompanyMember[]> {
        try {
            const url = `${COMPANY_SERVICE_BASE}/members/v1/${companyId}/list`;
            const token = localStorage.getItem('authToken');

            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || `Failed to fetch members: ${response.status}`);
            }

            const result = await response.json();

            return result.data || result;

        } catch (error: any) {
            console.error('❌ Failed to fetch company members:', error);
            throw new Error(error.message || 'Failed to fetch company members');
        }
    }

    /**
     * Get company statistics
     * Calls Go backend: GET /api/v1/companies/:id/stats
     *
     * @param companyId - Company UUID
     * @returns Company statistics
     */
    async getCompanyStatistics(companyId: string): Promise<CompanyStatsResponse> {
        try {
            const url = `${COMPANY_SERVICE_BASE}/companies/v1/${companyId}/stats`;
            const token = localStorage.getItem('authToken');

            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || `Failed to fetch stats: ${response.status}`);
            }

            const result = await response.json();

            return result.data || result;

        } catch (error: any) {
            console.error('❌ Failed to fetch company statistics:', error);
            throw new Error(error.message || 'Failed to fetch company statistics');
        }
    }

    /**
     * Remove a member from company team (owner only)
     * Calls Go backend: DELETE /members/v1/:companyId/:email
     *
     * @param companyId - Company UUID
     * @param memberEmail - Member email to remove (URL encoded)
     */
    async removeCompanyMember(companyId: string, memberEmail: string): Promise<void> {
        try {
            const url = `${COMPANY_SERVICE_BASE}/members/v1/${companyId}/${encodeURIComponent(memberEmail)}`;
            const token = localStorage.getItem('authToken');

            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }

            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || `Failed to remove member: ${response.status}`);
            }


        } catch (error: any) {
            console.error('❌ Failed to remove member:', error);
            throw new Error(error.message || 'Failed to remove member');
        }
    }
}

export const companyApiService = new CompanyApiServiceExtended();
